import React from 'react';
import type { ConsultationInvoice } from '../../../services/invoiceService';
import { formatDate } from '../../../lib/format';

interface Props {
    invoice: ConsultationInvoice;
}

const FISCAL_STATUS_LABELS: Record<string, string> = {
    pending: 'Pendiente',
    issued: 'Emitida',
    cancelled: 'Anulada',
};

const ECF_STATUS_LABELS: Record<string, string> = {
    pending: 'Pendiente',
    submitted: 'Enviado',
    accepted: 'Aceptado',
    rejected: 'Rechazado',
    error: 'Error',
};

const ECF_STATUS_CLASSES: Record<string, string> = {
    pending: 'warning',
    submitted: 'info',
    accepted: 'success',
    rejected: 'danger',
    error: 'dark',
};

/**
 * Read-only display of fiscal fields shown in InvoicePage view mode
 * after a fiscal invoice is created. The component intentionally
 * renders nothing for non-fiscal invoices so the host can drop it
 * anywhere without conditional checks.
 */
const FiscalInvoiceDetails: React.FC<Props> = ({ invoice }) => {
    if (!invoice.is_fiscal || !invoice.ncf_number) {
        return null;
    }

    const statusLabel = invoice.fiscal_status
        ? FISCAL_STATUS_LABELS[invoice.fiscal_status] ?? invoice.fiscal_status
        : '—';

    const formattedValidUntil = invoice.valid_until
        ? formatDate(invoice.valid_until)
        : null;

    return (
        <section
            aria-label="Información fiscal del comprobante"
            className="mt-3 p-3 rounded"
            style={{ background: '#fef2f2', border: '1px solid #fecaca' }}
        >
            <h6 className="fw-bold mb-2" style={{ color: '#991b1b' }}>
                <i className="fa fa-file-invoice me-1" /> Comprobante Fiscal
            </h6>
            <dl className="row mb-0 small">
                <dt className="col-sm-4 text-muted">Tipo NCF</dt>
                <dd className="col-sm-8 mb-1">
                    {invoice.ncf_type
                        ? `${invoice.ncf_type.code} — ${invoice.ncf_type.name}`
                        : '—'}
                </dd>

                <dt className="col-sm-4 text-muted">NCF</dt>
                <dd className="col-sm-8 mb-1">
                    <strong data-testid="fiscal-ncf-number">{invoice.ncf_number}</strong>
                </dd>

                <dt className="col-sm-4 text-muted">Estado fiscal</dt>
                <dd className="col-sm-8 mb-1">
                    <span
                        data-testid="fiscal-status-badge"
                        className="badge"
                        style={{ background: '#991b1b', color: '#fff' }}
                    >
                        {statusLabel}
                    </span>
                </dd>

                <dt className="col-sm-4 text-muted">RNC Emisor</dt>
                <dd className="col-sm-8 mb-1" data-testid="fiscal-issuer-rnc">
                    {invoice.issuer_rnc ?? '—'}
                </dd>

                {invoice.receiver_rnc && (
                    <>
                        <dt className="col-sm-4 text-muted">RNC/Cédula Receptor</dt>
                        <dd className="col-sm-8 mb-1">{invoice.receiver_rnc}</dd>
                    </>
                )}

                {formattedValidUntil && (
                    <>
                        <dt className="col-sm-4 text-muted">Válido hasta</dt>
                        <dd className="col-sm-8 mb-0" data-testid="fiscal-valid-until">
                            {formattedValidUntil}
                        </dd>
                    </>
                )}
            </dl>

            {/* e-CF Submission Status */}
            {invoice.ecf_submission_status && (
                <div
                    className="mt-3 p-2 rounded"
                    style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}
                    data-testid="ecf-status-section"
                >
                    <small className="text-uppercase text-muted fw-bold d-block mb-1">
                        <i className="fa fa-cloud-upload-alt me-1" /> e-CF DGII
                    </small>
                    <span
                        data-testid="ecf-status-badge"
                        className={`badge bg-${ECF_STATUS_CLASSES[invoice.ecf_submission_status] ?? 'secondary'}`}
                        style={{ fontSize: '12px' }}
                    >
                        {ECF_STATUS_LABELS[invoice.ecf_submission_status] ?? invoice.ecf_submission_status}
                    </span>
                    {invoice.provider_ref && (
                        <span className="ms-2 text-muted small" data-testid="ecf-provider-ref">
                            Ref: {invoice.provider_ref}
                        </span>
                    )}
                    {invoice.ecf_rejection_reason && invoice.ecf_submission_status === 'rejected' && (
                        <div className="mt-1 text-danger small" data-testid="ecf-rejection-reason">
                            {invoice.ecf_rejection_reason}
                        </div>
                    )}
                </div>
            )}
        </section>
    );
};

export default FiscalInvoiceDetails;
